import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/digitalTemplate/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/digitalTemplate/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/digitalTemplate/FETCH_FAILURE'

const CREATE_REQUEST = 'realhub/digitalTemplate/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/digitalTemplate/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/digitalTemplate/CREATE_FAILURE'

const UPDATE_REQUEST = 'realhub/digitalTemplate/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/digitalTemplate/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/digitalTemplate/UPDATE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  currentUsage: [],
  errors: [],
  loaded: false,
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(payload = {}){
  return {
    type: FETCH_SUCCESS,
    currentUsage: payload.currentUsage,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function loadDigitalTemplates(options){
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api('/admin/digital_templates.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.DIGITAL_TEMPLATE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function loadDigitalTemplate(templateId, options, callback){
  return (dispatch, getState) => {
    const state = getState()
    const loadedIds = [...state.digitalTemplates.loadedIds]
    if (!loadedIds.includes(templateId)){
      loadedIds.push(templateId)
    }

    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/admin/digital_templates/${templateId}.json`, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.DIGITAL_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function loadDigitalTemplateCurrentUsage(digitalTemplateId, options){
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/admin/digital_templates/${digitalTemplateId}/current_usage.json`, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        dispatch(fetchSuccess({ currentUsage: json }))

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function createDigitalTemplate(digitalTemplate, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      digital_template: digitalTemplate,
    }),
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api('/admin/digital_templates.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.DIGITAL_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        if (callback) callback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function updateDigitalTemplate(digitalTemplate, options, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      digital_template: digitalTemplate,
    }),
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/admin/digital_templates/${digitalTemplate.id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.DIGITAL_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function duplicateDigitalTemplate(digitalTemplate, options){
  const config = {
    method: 'POST',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(createRequest())

    const promise = api(`/admin/digital_templates/${digitalTemplate.id}/duplicate.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        // Add New Template
        const normalizedJson = normalize(json, Schemas.DIGITAL_TEMPLATE)
        dispatch(updateEntities(normalizedJson))

        // Update Existing
        const existingPage = {
          ...digitalTemplate,
          hidden: true,
        }

        const existingNormalized = normalize(existingPage, Schemas.DIGITAL_TEMPLATE)
        dispatch(updateEntities(existingNormalized))

        dispatch(createSuccess())

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        loadedIds: action.loadedIds || state.loadedIds,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        currentUsage: action.currentUsage || [],
        errors: [],
        loaded: true,
        loading: false,
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
